import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { compose } from "redux";
import { AnimatePresence } from "framer-motion";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import { actions as topikActions } from "../../../modules/topik";
import { ModalMessage } from "../../../components/message/ModalMessage";
import FullscreenPuffLoader from "../../../components/status/FullscreenPuffLoader";
import TopikComparisonView from "./comparison/TopikComparisonView";
import TopikIntroView from "./TopikIntroView";
import TopikObservationView from "./analysis/TopikObservationView";
import TopikSeriousnessView from "./analysis/TopikSeriousnessView";
import TopikIssuesView from "./analysis/TopikIssuesView";
import TopikRiskView from "./analysis/TopikRiskView";
import TopikSolutionView from "./analysis/TopikSolutionView";
import TopikContinueView from "./analysis/TopikContinueView";
import TopikRiskMitigationView from "./analysis/TopikRiskMitigationView";
import TopikSolutionMaximizerView from "./analysis/TopikSolutionMaximizerView";
import TopikGoalView from "./analysis/TopikGoalView";
import TopikCompleteView from "./TopikCompleteView";
import TopikProgressRail from "./progress/TopikProgressRail";
import TopikJourneyMap from "./progress/TopikJourneyMap";
import { formatRecoveryCode } from "./recoveryCode";
import { useKeyboardOpen } from "./useKeyboardOpen";

import "./topik.scss";

const ANALYSIS_VIEWS = {
  analysis_observation: TopikObservationView,
  analysis_seriousness: TopikSeriousnessView,
  analysis_issues: TopikIssuesView,
  analysis_risk: TopikRiskView,
  analysis_solution: TopikSolutionView,
  analysis_continue: TopikContinueView,
  analysis_risk_mitigation: TopikRiskMitigationView,
  analysis_solution_maximizer: TopikSolutionMaximizerView,
  analysis_goal: TopikGoalView,
};

// The intro (§12.1) shows only while the player has not answered a single comparison yet -
// derivable from the empty answered-comparisons history, so re-mounts show it harmlessly.
const shouldShowIntro = (session) => (
  (session.comparisons ? session.comparisons.length === 0 : true)
  && !!session.currentComparison
);

const TopikPlayView = ({ t, session = null, topikStatus, gameState, gameSubject, dispatch }) => {
  const [mapOpen, setMapOpen] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const [introDismissed, setIntroDismissed] = useState(false);
  const keyboardOpen = useKeyboardOpen();

  useEffect(() => {
    dispatch(topikActions.session.get.request());
  }, [dispatch]);

  // B1: a failed session fetch must not spin forever. Distinguish "still loading" from
  // "load failed" and offer an explicit retry rather than an infinite loader.
  if (!session) {
    if (topikStatus.error && !topikStatus.loading) {
      return (
        <main className="topik-view topik-play-view">
          <h1 className="topik-view__title">{t("topik-play-view.load-error.title")}</h1>
          <p className="topik-view__instruction">{t("topik-play-view.load-error.message")}</p>
          <button
            type="button"
            className="topik-view__submit-button"
            onClick={() => dispatch(topikActions.session.get.request())}
          >
            {t("topik-play-view.load-error.retry")}
          </button>
        </main>
      );
    }
    return <FullscreenPuffLoader />;
  }

  if (session.step === "complete") {
    return <TopikCompleteView />;
  }

  const gameClosed = gameState.state === "complete";

  const closedScreen = (
    <main className="topik-view topik-play-view">
      <h1 className="topik-view__title">{t("topik-play-view.closed.title")}</h1>
      <p className="topik-view__instruction">{t("topik-play-view.closed.message")}</p>
    </main>
  );

  // B2: the game-end check runs BEFORE the intro gate, so a player parked on the intro of an
  // ended game sees the closed screen instead of a Start button that would hit a dead game.
  if (gameClosed && session.currentComparison) {
    // Comparison steps carry no in-progress draft, so a closed game swaps straight to the
    // closed screen (the analysis steps get the gentler banner + intact inputs instead).
    return closedScreen;
  }

  if (!introDismissed && shouldShowIntro(session)) {
    return <TopikIntroView session={session} onStart={() => setIntroDismissed(true)} />;
  }

  const recoveryCode = formatRecoveryCode(session.recoveryCode);

  const rail = (
    // Truncation on short-circuit / stop-gate is already carried by the realized `steps`
    // (the backend truncates them), so the rail needs no separate short-circuit flag.
    <TopikProgressRail
      steps={session.steps}
      step={session.step}
      mapOpen={mapOpen}
      onOpenMap={() => setMapOpen(true)}
    />
  );

  const journeyMap = (
    <TopikJourneyMap
      open={mapOpen}
      session={session}
      onClose={() => setMapOpen(false)}
      onBack={() => dispatch(topikActions.analysis.back.request())}
    />
  );

  // B3: the recovery code is otherwise only shown on the intro; once Start is tapped it is
  // gone. A quiet header affordance reopens it in a modal at any point during play.
  const recoveryAffordance = recoveryCode && (
    <>
      <button
        type="button"
        className="topik-info-button topik-no-print"
        onClick={() => setCodeOpen(true)}
        aria-label={t("topik-play-view.recovery.open")}
      >
        <HelpOutlineIcon fontSize="small" />
      </button>
      <ModalMessage
        visible={codeOpen}
        title={t("topik-play-view.recovery.title")}
        message={`${t("topik-play-view.recovery.message")} ${recoveryCode}`}
        allowDismiss
        dismissButtonText={t("topik-play-view.recovery.dismiss")}
        onDismiss={() => setCodeOpen(false)}
      />
    </>
  );

  // Persistent chrome (§1.2-1): the rail + journey map never unmount, and the step content
  // swaps through AnimatePresence so the old step animates out and the new one in (exit +
  // enter, no white flash / teleport). `initial={false}` suppresses a sweep on first mount;
  // `exitBeforeEnter` (framer v3) is mode="wait" - one step is on screen at a time. B8: the
  // rail is hidden while the on-screen keyboard is up so the cramped analysis steps fit.
  // The shell owns the viewport height (min-height: 100dvh): the rail sits *above* the step
  // content in flow, so if the `.topik-view` also claimed 100dvh the two would stack past the
  // viewport and leave a rail-height band of phantom scroll below the fold. Wrapping them in
  // one flex column lets the content fill only the space the rail leaves.
  const chrome = (content) => (
    <>
      {recoveryAffordance}
      <div className="topik-play-shell">
        {gameSubject && <p className="topik-play-shell__subject">{gameSubject}</p>}
        {!keyboardOpen && rail}
        <AnimatePresence initial={false} exitBeforeEnter>
          {content}
        </AnimatePresence>
      </div>
      {journeyMap}
    </>
  );

  if (session.currentComparison) {
    // Key on id AND question: generality and outcome for one pair are the same backend
    // comparison row (same id), so keying on id alone would not remount when the step advances
    // from generality->outcome - the frozen useState snapshot in TopikComparisonView would keep
    // rendering the generality step even though the store (and backend) moved on.
    const { id, question } = session.currentComparison;
    return chrome(<TopikComparisonView key={`${id}-${question}`} />);
  }

  const AnalysisView = ANALYSIS_VIEWS[session.step];
  if (AnalysisView) {
    return chrome(<AnalysisView key={session.step} gameClosed={gameClosed} />);
  }

  throw new Error(`Unknown topik step: '${session.step}'`);
};

TopikPlayView.propTypes = {
  t: PropTypes.func.isRequired,
  session: PropTypes.shape({
    step: PropTypes.string.isRequired,
    steps: PropTypes.arrayOf(PropTypes.string).isRequired,
    currentComparison: PropTypes.object,
    comparisons: PropTypes.arrayOf(PropTypes.object),
    recoveryCode: PropTypes.string,
    shortCircuited: PropTypes.bool,
  }),
  topikStatus: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.any,
  }).isRequired,
  gameState: PropTypes.object.isRequired,
  gameSubject: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  session: state.topik.session,
  topikStatus: state.topik.status,
  gameState: state.game.gameState,
  gameSubject: state.game.game ? state.game.game.subject : null,
});

export default compose(withTranslation("views"), connect(mapStateToProps))(TopikPlayView);
